'use client';

import { c as _c } from "react/compiler-runtime";
import { MapPin, Train } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import type { MouseEvent } from 'react';
import type { TagItem } from '~/components/ItineraryCard/src/ItineraryCard.types';
import { ItineraryCardTags } from '~/components/ItineraryCard/src/ItineraryCardTags';
import { visuallyHidden } from '~/components/Utilities';
import type { AnyJourneyItemTransformed, LegTransformed, StayTransformed } from '~/types/JourneyItem.types';
import styles from './ExpandedItineraryScroller.module.css';
export type AccommodationScrollerStayLabel = {
  hotelName: string;
  location: string;
};
export type CompactItineraryScrollerCardProps = {
  item: AnyJourneyItemTransformed;
  isReturnLeg?: boolean;
  isActive: boolean;
  onCardClick: (cardEl: HTMLElement) => void;
  accommodationLabel?: AccommodationScrollerStayLabel;
  'data-test-id'?: string;
};
const CardImage = t0 => {
  const $ = _c(2);
  const {
    src
  } = t0;
  let t1;
  if ($[0] !== src) {
    t1 = src ? <img src={src} alt="" className={styles.cardImage} /> : <div className={styles.cardImagePlaceholder} />;
    $[0] = src;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const CompactItineraryScrollerCard = t0 => {
  const $ = _c(83);
  const {
    item,
    isReturnLeg: t1,
    isActive,
    onCardClick,
    accommodationLabel,
    "data-test-id": dataTestId
  } = t0;
  const isReturnLeg = t1 === undefined ? false : t1;
  const t = useTranslations("journey");
  const tAria = useTranslations("aria");
  let t2;
  if ($[0] !== onCardClick) {
    t2 = event => {
      onCardClick(event.currentTarget);
    };
    $[0] = onCardClick;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleClick = t2;
  if (item.type === "Journey") {
    const leg = item as LegTransformed;
    const featured = leg.heroRouteCmsId != null && !isReturnLeg;
    const t3 = leg.origin;
    let t4;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <span aria-hidden="true"> → </span>;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    let t5;
    if ($[3] !== tAria) {
      t5 = tAria("to");
      $[3] = tAria;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    const t6 = ` ${t5} `;
    let t7;
    if ($[5] !== t6) {
      t7 = <span className={visuallyHidden}>{t6}</span>;
      $[5] = t6;
      $[6] = t7;
    } else {
      t7 = $[6];
    }
    let t8;
    if ($[7] !== leg.destination || $[8] !== leg.origin || $[9] !== t7) {
      t8 = <>{t3}{t4}{t7}{leg.destination}</>;
      $[7] = leg.destination;
      $[8] = leg.origin;
      $[9] = t7;
      $[10] = t8;
    } else {
      t8 = $[10];
    }
    const routeTitle = t8;
    if (featured) {
      let t9;
      if ($[11] !== leg.images) {
        t9 = leg.images?.find(_temp)?.src;
        $[11] = leg.images;
        $[12] = t9;
      } else {
        t9 = $[12];
      }
      const image = t9;
      let t10;
      if ($[13] !== t) {
        t10 = t("overview_hero_route_tag");
        $[13] = t;
        $[14] = t10;
      } else {
        t10 = $[14];
      }
      let t11;
      if ($[15] !== t10) {
        t11 = [{
          label: t10,
          variant: "hero"
        }];
        $[15] = t10;
        $[16] = t11;
      } else {
        t11 = $[16];
      }
      const tags = t11;
      const t12 = isActive && styles.cardActive;
      let t13;
      if ($[17] !== t12) {
        t13 = clsx(styles.card, styles.cardWithImage, t12);
        $[17] = t12;
        $[18] = t13;
      } else {
        t13 = $[18];
      }
      let t14;
      if ($[19] !== image) {
        t14 = <CardImage src={image} />;
        $[19] = image;
        $[20] = t14;
      } else {
        t14 = $[20];
      }
      const t15 = leg.heroRouteTitle ?? routeTitle;
      let t16;
      if ($[21] !== t15) {
        t16 = <div className={styles.cardTitleRow}><span className={styles.cardTitle}>{t15}</span></div>;
        $[21] = t15;
        $[22] = t16;
      } else {
        t16 = $[22];
      }
      let t17;
      if ($[23] !== tags) {
        t17 = <div className={styles.cardTagRow}><ItineraryCardTags tags={tags} /></div>;
        $[23] = tags;
        $[24] = t17;
      } else {
        t17 = $[24];
      }
      let t18;
      if ($[25] !== t16 || $[26] !== t17) {
        t18 = <div className={styles.cardContent}>{t16}{t17}</div>;
        $[25] = t16;
        $[26] = t17;
        $[27] = t18;
      } else {
        t18 = $[27];
      }
      let t19;
      if ($[28] !== dataTestId || $[29] !== handleClick || $[30] !== item.id || $[31] !== t13 || $[32] !== t14 || $[33] !== t18) {
        t19 = <button type="button" className={t13} data-card-id={item.id} data-test-id={dataTestId} onMouseDown={_temp2} onClick={handleClick}>{t14}{t18}</button>;
        $[28] = dataTestId;
        $[29] = handleClick;
        $[30] = item.id;
        $[31] = t13;
        $[32] = t14;
        $[33] = t18;
        $[34] = t19;
      } else {
        t19 = $[34];
      }
      return t19;
    }
    const t9 = isActive && styles.cardActive;
    let t10;
    if ($[35] !== t9) {
      t10 = clsx(styles.card, styles.cardLeg, t9);
      $[35] = t9;
      $[36] = t10;
    } else {
      t10 = $[36];
    }
    let t11;
    if ($[37] !== routeTitle) {
      t11 = <span className={styles.cardTitle}>{routeTitle}</span>;
      $[37] = routeTitle;
      $[38] = t11;
    } else {
      t11 = $[38];
    }
    let t12;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <div className={styles.cardLegIcon}><Train size="small" decorative={true} /></div>;
      $[39] = t12;
    } else {
      t12 = $[39];
    }
    let t13;
    if ($[40] !== dataTestId || $[41] !== handleClick || $[42] !== item.id || $[43] !== t10 || $[44] !== t11) {
      t13 = <button type="button" className={t10} data-card-id={item.id} data-test-id={dataTestId} onMouseDown={_temp3} onClick={handleClick}>{t11}{t12}</button>;
      $[40] = dataTestId;
      $[41] = handleClick;
      $[42] = item.id;
      $[43] = t10;
      $[44] = t11;
      $[45] = t13;
    } else {
      t13 = $[45];
    }
    return t13;
  }
  if (item.type === "Stay") {
    const stay = item as StayTransformed;
    let t3;
    if ($[46] !== stay.images) {
      t3 = stay.images?.find(_temp4)?.src;
      $[46] = stay.images;
      $[47] = t3;
    } else {
      t3 = $[47];
    }
    const image_0 = t3;
    if (accommodationLabel) {
      const t4 = isActive && styles.cardAccommodationActive;
      let t5;
      if ($[48] !== t4) {
        t5 = clsx(styles.card, styles.cardWithImage, styles.cardAccommodation, t4);
        $[48] = t4;
        $[49] = t5;
      } else {
        t5 = $[49];
      }
      let t6;
      if ($[50] !== image_0) {
        t6 = <CardImage src={image_0} />;
        $[50] = image_0;
        $[51] = t6;
      } else {
        t6 = $[51];
      }
      let t7;
      if ($[52] !== accommodationLabel.hotelName) {
        t7 = <span className={styles.cardTitleAccommodation}>{accommodationLabel.hotelName}</span>;
        $[52] = accommodationLabel.hotelName;
        $[53] = t7;
      } else {
        t7 = $[53];
      }
      let t8;
      if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = <MapPin decorative={true} size="small" />;
        $[54] = t8;
      } else {
        t8 = $[54];
      }
      let t9;
      if ($[55] !== accommodationLabel.location) {
        t9 = <span className={styles.locationPill}>{t8}<span className={styles.locationPillText}>{accommodationLabel.location}</span></span>;
        $[55] = accommodationLabel.location;
        $[56] = t9;
      } else {
        t9 = $[56];
      }
      let t10;
      if ($[57] !== t7 || $[58] !== t9) {
        t10 = <div className={styles.cardContent}>{t7}{t9}</div>;
        $[57] = t7;
        $[58] = t9;
        $[59] = t10;
      } else {
        t10 = $[59];
      }
      let t11;
      if ($[60] !== dataTestId || $[61] !== handleClick || $[62] !== item.id || $[63] !== t10 || $[64] !== t5 || $[65] !== t6) {
        t11 = <button type="button" className={t5} data-card-id={item.id} data-test-id={dataTestId} onMouseDown={_temp5} onClick={handleClick}>{t6}{t10}</button>;
        $[60] = dataTestId;
        $[61] = handleClick;
        $[62] = item.id;
        $[63] = t10;
        $[64] = t5;
        $[65] = t6;
        $[66] = t11;
      } else {
        t11 = $[66];
      }
      return t11;
    }
    const t4 = isActive && styles.cardActive;
    let t5;
    if ($[67] !== t4) {
      t5 = clsx(styles.card, styles.cardWithImage, t4);
      $[67] = t4;
      $[68] = t5;
    } else {
      t5 = $[68];
    }
    const t6 = item.id;
    let t7;
    if ($[69] !== image_0) {
      t7 = <CardImage src={image_0} />;
      $[69] = image_0;
      $[70] = t7;
    } else {
      t7 = $[70];
    }
    let t8;
    if ($[71] !== stay.title || $[72] !== t) {
      t8 = t("itinerary_stay_in", {
        place: stay.title
      });
      $[71] = stay.title;
      $[72] = t;
      $[73] = t8;
    } else {
      t8 = $[73];
    }
    let t9;
    if ($[74] !== t8) {
      t9 = <div className={styles.cardContent}><div className={styles.cardTitleRow}><span className={styles.cardTitle}>{t8}</span></div></div>;
      $[74] = t8;
      $[75] = t9;
    } else {
      t9 = $[75];
    }
    let t10;
    if ($[76] !== dataTestId || $[77] !== handleClick || $[78] !== item.id || $[79] !== t5 || $[80] !== t7 || $[81] !== t9) {
      t10 = <button type="button" className={t5} data-card-id={t6} data-test-id={dataTestId} onMouseDown={_temp6} onClick={handleClick}>{t7}{t9}</button>;
      $[76] = dataTestId;
      $[77] = handleClick;
      $[78] = item.id;
      $[79] = t5;
      $[80] = t7;
      $[81] = t9;
      $[82] = t10;
    } else {
      t10 = $[82];
    }
    return t10;
  }
  return null;
};
function _temp(img) {
  return img.src;
}
function _temp2(event_0) {
  return event_0.preventDefault();
}
function _temp3(event_1) {
  return event_1.preventDefault();
}
function _temp4(img_0) {
  return img_0.src;
}
function _temp5(event_2) {
  return event_2.preventDefault();
}
function _temp6(event_3) {
  return event_3.preventDefault();
}